/**
 * Sanitizes a tool name to comply with Verbeo's native audio tool naming rules:
 * - Must start with a letter or underscore (not a number)
 * - Can contain letters, numbers, and underscores
 * - No spaces (replaced with underscores)
 * - No special characters except underscore
 * - Converted to lowercase
 * 
 * @param toolName - The original tool name
 * @returns Sanitized tool name that complies with Verbeo's rules
 */
export function sanitizeToolName(toolName: string): string {
  if (!toolName || typeof toolName !== 'string') {
    return '';
  }

  // Convert to lowercase
  let sanitized = toolName.toLowerCase().trim();

  // Replace spaces and special characters (except underscores) with underscores
  sanitized = sanitized.replace(/[^a-z0-9_]/g, '_');

  // Remove consecutive underscores
  sanitized = sanitized.replace(/_+/g, '_');

  // Remove leading/trailing underscores
  sanitized = sanitized.replace(/^_+|_+$/g, '');

  // If starts with a number, prefix with underscore
  if (/^\d/.test(sanitized)) {
    sanitized = '_' + sanitized;
  }

  // If empty after sanitization, use a default
  if (!sanitized) {
    sanitized = 'tool';
  }

  return sanitized;
}

/**
 * Validates if a tool name complies with Verbeo's native audio tool naming rules
 * 
 * @param toolName - The tool name to validate
 * @returns Object with isValid flag and error message if invalid
 */
export function validateToolName(toolName: string): { isValid: boolean; error?: string } {
  if (!toolName || typeof toolName !== 'string' || !toolName.trim()) {
    return { isValid: false, error: 'Tool name is required' };
  }

  const trimmed = toolName.trim();

  // Check if starts with a number
  if (/^\d/.test(trimmed)) {
    return { 
      isValid: false, 
      error: 'Tool name cannot start with a number. It must start with a letter or underscore.' 
    };
  }

  // Check for invalid characters (only letters, numbers, and underscores allowed)
  if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(trimmed)) {
    return { 
      isValid: false, 
      error: 'Tool name can only contain letters, numbers, and underscores. Use underscores instead of spaces.' 
    };
  }

  return { isValid: true };
}

/**
 * Sanitizes parameter property names to comply with Verbeo's rules
 * 
 * @param paramName - The parameter name to sanitize
 * @returns Sanitized parameter name
 */
export function sanitizeParameterName(paramName: string): string {
  return sanitizeToolName(paramName);
}

/**
 * List of JSON Schema keywords not supported by Verbeo
 */
const UNSUPPORTED_SCHEMA_KEYWORDS = ['const', 'oneOf', 'anyOf', 'allOf', 'not', '$ref', 'if', 'then', 'else'];

/** One node of a parameters schema, as parsed from the dialog's JSON. */
type SchemaNode = Record<string, unknown>;

const isSchemaNode = (value: unknown): value is SchemaNode =>
  typeof value === 'object' && value !== null;

/**
 * Validates parameter schema for Verbeo compatibility.
 * Checks for unsupported JSON Schema keywords like 'const'.
 *
 * @param params - The parameters object to validate
 * @param nested - Inside another parameter: its names are sent as they are, not cleaned
 * @returns Error message if invalid, undefined if valid
 */
export function validateParameterSchema(params: unknown, nested = false): string | undefined {
  if (!isSchemaNode(params) || !isSchemaNode(params.properties)) {
    return undefined;
  }

  for (const [paramName, paramDef] of Object.entries(params.properties)) {
    // Parsed JSON, so a property may be anything: the `tool` function reads
    // keywords off it, and `{"a": null}` fails there with a 500.
    if (!isSchemaNode(paramDef) || Array.isArray(paramDef)) {
      return `Parameter "${paramName}" must be a schema object, e.g. {"type": "string"}.`;
    }
    const def = paramDef;

    // Check for unsupported keywords
    for (const keyword of UNSUPPORTED_SCHEMA_KEYWORDS) {
      if (def[keyword] !== undefined) {
        if (keyword === 'const') {
          return `Parameter "${paramName}" uses "const" which is not supported by Verbeo. Use "enum" with a single value instead. Example: "enum": ["${def.const}"]`;
        }
        return `Parameter "${paramName}" uses "${keyword}" which is not supported by Verbeo.`;
      }
    }

    // Validate parameter name
    const validation = validateToolName(paramName);
    if (!validation.isValid) {
      return `Invalid parameter name "${paramName}": ${validation.error}`;
    }
    // validateToolName trims, and only the top level's names are cleaned before sending.
    if (nested && paramName !== paramName.trim()) {
      return `Invalid parameter name "${paramName}": remove the spaces around it.`;
    }

    // Recursively check nested objects
    if (def.type === 'object' && def.properties) {
      const nestedError = validateParameterSchema(def, true);
      if (nestedError) {
        return nestedError;
      }
    }

    // Check array items
    if (def.type === 'array' && def.items) {
      const itemsError = validateParameterSchema({ properties: { item: def.items } }, true);
      if (itemsError) {
        return itemsError.replace('Parameter "item"', `Array items in "${paramName}"`);
      }
    }
  }

  return undefined;
}

/**
 * What both tool dialogs check before sending a parameters schema — the `tool`
 * function's rules: `{}` is a tool without arguments, anything else needs a
 * "properties" object; then the schema itself (keywords and property names);
 * then `required`, a list of parameter names.
 */
export function validateParameters(params: SchemaNode): string | undefined {
  const { properties, required = [] } = params;
  if (Object.keys(params).length > 0 && (!isSchemaNode(properties) || Array.isArray(properties))) {
    return 'Parameters must have a "properties" object';
  }

  const schemaError = validateParameterSchema(params);
  if (schemaError) {
    return schemaError;
  }

  if (!Array.isArray(required)) {
    return '"required" must be a list of parameter names';
  }
  for (const reqName of required) {
    if (typeof reqName !== 'string') {
      return '"required" must be a list of parameter names';
    }
    const validation = validateToolName(reqName);
    if (!validation.isValid) {
      return `Invalid required parameter name "${reqName}": ${validation.error}`;
    }
  }

  return undefined;
}

/**
 * What is wrong with the parameters box's text, or undefined when it is empty or
 * a schema Verbeo accepts. The tool dialogs show it under the box and send
 * nothing while it answers anything.
 */
export function parametersJsonError(json: string): string | undefined {
  if (!json.trim()) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (e) {
    return `Invalid JSON: ${(e as Error).message}`;
  }
  if (!isSchemaNode(parsed) || Array.isArray(parsed)) {
    return "Parameters must be a valid JSON object";
  }
  return validateParameters(parsed);
}

/**
 * Removes an execution-protocol block older tools have baked into their
 * description — the speak ("[CRITICAL PROTOCOL: … Speak, then execute.") and the
 * silent ("[BACKGROUND PROCESS: SILENT EXECUTION] …") one.
 */
export function stripProtocolFromDescription(description: string): string {
  return description
    .replace(/\[CRITICAL PROTOCOL:[\s\S]*?Speak, then execute\./g, '')
    .replace(/\[BACKGROUND PROCESS:[\s\S]*?Immediate execution is required\./g, '')
    .trim();
}

/**
 * A transfer tool keeps its dynamic prompt inside the description, after the
 * `[DYNAMIC PROMPT]` marker the `tool` function splits on too.
 */
export function splitDynamicPrompt(description: string | null): { description: string; prompt: string } {
  const text = description ?? '';
  const match = text.match(/\[DYNAMIC PROMPT\]\n([\s\S]*?)(?=\n\n\[CRITICAL PROTOCOL:|\n\n\[BACKGROUND PROCESS:|$)/);
  return {
    description: stripProtocolFromDescription(
      text.replace(/\[DYNAMIC PROMPT\][\s\S]*?(?=\n\n\[CRITICAL PROTOCOL:|\n\n\[BACKGROUND PROCESS:|$)/g, ''),
    ),
    prompt: match ? match[1].trim() : '',
  };
}
